import React, { useState, useEffect } from 'react';
import { toggleEventLike } from '@/apiEndpoints';
import { useAuth } from '@/contexts/auth-context';
import { useQueryClient } from '@tanstack/react-query';

interface LikeStats {
    totalLikes: number;
    isLikedByCurrentUser: boolean;
    eventId: string;
}

interface LikeButtonProps {
    eventId: string;
    initialLikes?: number;
    isLiked?: boolean;
    likeStats?: LikeStats;
    className?: string;
    onLikeChange?: (likes: number, isLiked: boolean) => void;
    refetchQueries?: string[]; // Array of query keys to refetch after like change
    iconOnly?: boolean; // Frosted-glass heart-only badge (no "Interested" label)
}

export default function LikeButton({
    eventId,
    initialLikes = 0,
    isLiked: initialIsLiked = false,
    likeStats,
    className = "",
    onLikeChange,
    refetchQueries = [],
    iconOnly = false
}: LikeButtonProps) {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const [likes, setLikes] = useState(initialLikes);
    const [isLiked, setIsLiked] = useState(initialIsLiked);
    const [isLoading, setIsLoading] = useState(false);

    // Update state when likeStats prop changes
    useEffect(() => {
        if (likeStats) {
            setLikes(likeStats.totalLikes || 0);
            // Only show user's like status if authenticated
            setIsLiked(isAuthenticated ? (likeStats.isLikedByCurrentUser || false) : false);
        } else {
            // Fallback to initial props if likeStats not provided
            setLikes(initialLikes);
            // Only show user's like status if authenticated
            setIsLiked(isAuthenticated ? initialIsLiked : false);
        }
    }, [likeStats, initialLikes, initialIsLiked, isAuthenticated]);

    const handleLikeToggle = async (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent event card navigation
        e.stopPropagation();

        // Only allow authenticated users to like/unlike
        if (!isAuthenticated) {
            console.log('User must be authenticated to like events');
            return;
        }

        if (isLoading) return;

        setIsLoading(true);
        try {
            const response = await toggleEventLike(eventId);
            if (response?.success) {
                console.log('response2', response);
                const newIsLiked = response?.data?.isLikedByCurrentUser;
                const newLikes = response?.data?.totalLikes;

                setIsLiked(newIsLiked);
                setLikes(newLikes);

                // Call parent callback if provided
                if (onLikeChange) {
                    onLikeChange(newLikes, newIsLiked);
                }

                // Refetch specified queries to update data
                if (refetchQueries.length > 0) {
                    await Promise.all(
                        refetchQueries.map(queryKey =>
                            queryClient.invalidateQueries({ queryKey: [queryKey] })
                        )
                    );
                }

                // Also invalidate event-specific queries
                await queryClient.invalidateQueries({
                    queryKey: ['event', eventId]
                });
                await queryClient.invalidateQueries({
                    queryKey: ['events']
                });
                await queryClient.invalidateQueries({
                    queryKey: ['relatedEvents']
                });
            }

        } catch (error) {
            console.error('Error toggling like:', error);
        } finally {
            setIsLoading(false);
        }
    };
    if (iconOnly) {
        return (
            <button
                onClick={handleLikeToggle}
                disabled={isLoading || !isAuthenticated}
                className={`flex items-center justify-center size-[24px] p-1 rounded opacity-90 transition-colors ${isAuthenticated ? 'cursor-pointer' : 'cursor-not-allowed'
                    } ${className}`}
                title={!isAuthenticated ? 'Please login to like this event' : ''}
            >
                {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={isLiked ? '#ef4444' : '#242424'} xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke={isLiked ? '#ef4444' : '#ffffff'} strokeWidth="1.5" />
                    </svg>
                )}
            </button>
        );
    }

    return (
        <button
            onClick={handleLikeToggle}
            disabled={isLoading || !isAuthenticated}
            className={`bg-grey99/90 text-grey20 text-xs h-[28px] font-medium px-2 py-1 rounded flex items-center gap-1 shadow transition-colors ${isAuthenticated
                ? 'hover:bg-grey99 cursor-pointer'
                : 'cursor-not-allowed opacity-60'
                } ${className}`}
            title={!isAuthenticated ? 'Please login to like this event' : ''}
        >
            {isLoading ? (
                <div className="w-4 h-4 border-2 border-grey20 border-t-transparent rounded-full animate-spin" />
            ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill={isLiked ? '#ef4444' : '#242424'} className={isLiked ? 'text-[#ef4444]' : 'text-grey40 dark:text-nightTextSecondary'} xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" strokeWidth="1.5" />
                </svg>
            )}
            <span className={isLiked ? 'text-[#ef4444]' : 'text-grey20 dark:text-nightTextSecondary'}>Interested</span>
        </button>
    );
}